/*
 *
 * CrossPiecePanel
 *
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer } from 'utils/injectReducer';
import { createStructuredSelector } from 'reselect';
import reducer from './reducer';
import { RootState } from './types';
import Panel from '../../components/Panel';
import { setColorsPanelAction } from '../Configurator/actions/general';
import {
  makeSelectContext,
  makeSelectIsVisibleColorsPanel,
} from '../Configurator/selectors/general';
import { ActivePanel } from '../Configurator/constants/general';
import { useTranslation } from 'react-i18next';
import CrossPieceSelector from './CrossPieceSelector';
import {
  CabinetSupport,
  CrossPieceType,
  Position,
} from '../../lib/modelBuilder/constants';
import F1Img from 'assets/images/cross_pieces/F1.png';
import F2Img from 'assets/images/cross_pieces/F2.png';
import G1Img from 'assets/images/cross_pieces/G1.png';
import G2Img from 'assets/images/cross_pieces/G2.png';
import G1RImg from 'assets/images/cross_pieces/G1R.png';
import G2RImg from 'assets/images/cross_pieces/G2R.png';
import BigOptionButton from '../../components/BigOptionButton';
import { addOrUpdateCrossPieceAction, removeCrossPieceAction } from './actions';
import { restoreCabinet } from '../Configurator/actions/furniture';
import {
  makeSelectIsLastShelvesSelected,
  makeSelectLastHistoryCabinet,
} from '../Configurator/selectors/history';
import { Cabinet } from '../../lib/modelBuilder/entities/Cabinet';
import { CustomPiece } from '../../lib/modelBuilder/pieces/CustomPiece';
import { CrossPiece } from '../../lib/modelBuilder/pieces/CrossPiece';
import { makeSelectShelvesSelectedLastCabinet } from '../Configurator/selectors/selection';
import { Shelf } from '../../lib/modelBuilder/entities/Shelf';
import {
  acceptedCrossPieceTypesForShelf,
  acceptedCrossPieceTypesForTop,
  getDefaultCrossPieceType,
} from '../../lib/modelBuilder/builder/crossPiecesManager';
import { PickerTitleCategory } from '../../components/PickerTitleCategory';
import ReactTooltip from 'react-tooltip';
import RemoveButton from '../../components/RemoveButton';
import { FiInfo } from 'react-icons/fi';
import { AiOutlineWarning } from 'react-icons/all';
import { makeSelectIsFurnitureMode } from '../Configurator/selectors/furniture';
import Checkbox from '@material-ui/core/Checkbox';
import { fixToWallAction } from '../Ribbon/CabinetRibbon/CabinetOptions/actions';
import { FurnitureEditorContext } from '../../lib/constraintsChecker/FurnitureEditor';
import { isProduction } from '../../utils/constants';

interface StateProps {
  isVisible: boolean;
  cabinet: Cabinet;
  shelvesSelected: Shelf[];
  isLastShelf: boolean;
  isFurnitureMode: boolean;
  context: FurnitureEditorContext;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  isVisible: makeSelectIsVisibleColorsPanel(ActivePanel.crossPieces),
  cabinet: makeSelectLastHistoryCabinet(),
  shelvesSelected: makeSelectShelvesSelectedLastCabinet(),
  isLastShelf: makeSelectIsLastShelvesSelected(),
  isFurnitureMode: makeSelectIsFurnitureMode(),
  context: makeSelectContext(),
});

function CrossPiecePanel() {
  useInjectReducer({ key: 'crossPiecePanel', reducer: reducer });
  const dispatch = useDispatch();
  const {
    isVisible,
    cabinet,
    shelvesSelected,
    isLastShelf,
    isFurnitureMode,
    context,
  } = useSelector(stateSelector);
  const { t } = useTranslation();

  const [selectorPositionCP, setSelectorPositionCP] = useState(Position.Front);
  const [selectedInferior, setSelectedInferior] = useState(true);
  const [reinforceCategory, setReinforceCategory] = useState(false);

  useEffect(() => {
    if (!isLastShelf && !selectedInferior) {
      setSelectedInferior(true);
    }
  }, [isLastShelf]);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [reinforceCategory, selectorPositionCP, selectedInferior]);

  const hidePanel = () => {
    dispatch(setColorsPanelAction());
  };

  const onLeave = () => {
    // Restore the last history element
    dispatch(restoreCabinet(cabinet));
  };

  const setDrill = (value: boolean) => {
    dispatch(
      addOrUpdateCrossPieceAction(
        selectorPositionCP,
        selectedInferior,
        CrossPieceType.UpperPaneLowerPane,
        false,
        false,
        value,
      ),
    );
  };

  const shelf = shelvesSelected[0];
  // const positionsAccepted: (CrossPieceType | undefined)[]; //= acceptedCrossPieceTypesForShelf(shelf, shelf.rack(0), )

  if (!shelf || isFurnitureMode) {
    if (isVisible) {
      hidePanel();
    }
    return null;
  }

  // [PP, EP, undefined, DD]
  const positionsAccepted = new Map<Position, (CrossPieceType | undefined)[]>();
  for (const position of Object.values(Position)) {
    positionsAccepted.set(
      position,
      acceptedCrossPieceTypesForShelf(shelf, shelf.rack(0), position),
    );
  }

  const positionsTopAccepted = new Map<
    Position,
    (CrossPieceType | undefined)[]
  >();
  for (const position of Object.values(Position)) {
    positionsTopAccepted.set(
      position,
      acceptedCrossPieceTypesForTop(shelf, position),
    );
  }

  // const activeCrossPiece =

  const isEnable = (type: CrossPieceType) => {
    if (!selectedInferior) {
      return positionsTopAccepted.get(selectorPositionCP)?.includes(type);
    }
    return positionsAccepted.get(selectorPositionCP)?.includes(type);
  };

  const isDrill = (): boolean => {
    let cp: CustomPiece<CrossPiece> | undefined;
    if (!selectedInferior) cp = cabinet.top.getCrossPiece(selectorPositionCP);
    else cp = shelf.rack(0).getCrossPiece(selectorPositionCP);
    if (cp) return !!cp.base.drill;

    return false;
  };

  const isSelected = (type: CrossPieceType) => {
    let cp: CustomPiece<CrossPiece> | undefined;
    if (!selectedInferior) cp = cabinet.top.getCrossPiece(selectorPositionCP);
    else cp = shelf.rack(0).getCrossPiece(selectorPositionCP);
    if (cp)
      return cp.base.type === type && cp.base.reinforce === reinforceCategory;

    return false;
  };

  const onSelect = (position: Position, inferior: boolean) => {
    setSelectorPositionCP(position);
    setSelectedInferior(inferior);

    // Add if no cross piece exist at this position
    let crossPieces: CustomPiece<CrossPiece>[];
    let positionsAcceptedSelected: Map<
      Position,
      (CrossPieceType | undefined)[]
    >;
    if (inferior) {
      crossPieces = shelf.rack(0).crossPieces;
      positionsAcceptedSelected = positionsAccepted;
    } else {
      crossPieces = cabinet.top.crossPieces;
      positionsAcceptedSelected = positionsTopAccepted;
    }
    if (!crossPieces.find(e => e.position === position)) {
      dispatch(
        addOrUpdateCrossPieceAction(
          position,
          inferior,
          getDefaultCrossPieceType(
            positionsAcceptedSelected.get(position) as (
              | CrossPieceType
              | undefined
            )[],
          ),
          false,
          false,
        ),
      );
    }
  };

  const canDelete = selectedInferior
    ? positionsAccepted.get(selectorPositionCP)?.includes(undefined)
    : positionsTopAccepted.get(selectorPositionCP)?.includes(undefined);

  const canRemoveDrill = !(
    cabinet.support === CabinetSupport.ToHangScrew &&
    selectorPositionCP === Position.Back &&
    ((selectedInferior && shelf.level === 0) || !selectedInferior)
  );

  return (
    <Panel close={hidePanel} isVisible={isVisible} autoclose={false}>
      <div className="flex justify-center w-full h-full">
        <div className="w-2/5 flex items-center h-full">
          <CrossPieceSelector
            title={t('Lower cross pieces')}
            inferior={true}
            selected={selectorPositionCP}
            selectedPositionInferior={selectedInferior}
            onSelect={onSelect}
            crossPieces={shelf.rack(0).crossPieces}
          />
          {isLastShelf && (
            <CrossPieceSelector
              title={t('Upper cross pieces')}
              inferior={false}
              selected={selectorPositionCP}
              selectedPositionInferior={selectedInferior}
              onSelect={onSelect}
              crossPieces={cabinet.top.crossPieces}
            />
          )}

          {canDelete && canRemoveDrill ? (
            <div data-for="removeCrossPieceInfo" data-tip>
              <RemoveButton
                onClick={() => {
                  dispatch(
                    removeCrossPieceAction(
                      selectorPositionCP,
                      selectedInferior,
                    ),
                  );
                }}
              />
            </div>
          ) : (
            <div
              data-for="cannotRemoveCrossPieceInfo"
              data-tip
              className="opacity-25"
            >
              <RemoveButton onClick={null} className="cursor-not-allowed" />
            </div>
          )}
        </div>

        {(context === FurnitureEditorContext.sales || !isProduction) &&
          selectorPositionCP !== Position.Front &&
          (canRemoveDrill ? (
            <div className="flex flex-col justify-center items-center">
              <Checkbox
                color="default"
                checked={isDrill()}
                onChange={() => {
                  setDrill(!isDrill());

                  if (
                    cabinet.support !== CabinetSupport.ToHangScrew &&
                    !selectedInferior
                  )
                    dispatch(fixToWallAction(!isDrill()));
                }}
              />
              <div>{t('Fix to wall')}</div>
            </div>
          ) : (
            <div
              className="flex flex-col justify-center items-center opacity-25"
              data-for="cannotRemoveDrillCrossPieceInfo"
              data-tip
            >
              <Checkbox color="default" checked={isDrill()} disabled />
              <div>{t('Fix to wall')}</div>
            </div>
          ))}

        <div className="w-2/5 xl: ml-4 bg-white">
          <div className="flex bg-gray-200">
            <PickerTitleCategory
              selected={!reinforceCategory}
              selectable
              onClick={() => {
                setReinforceCategory(false);
                ReactTooltip.rebuild();
              }}
            >
              {t('Standard')}
            </PickerTitleCategory>
            <PickerTitleCategory
              selected={reinforceCategory}
              selectable
              onClick={() => {
                setReinforceCategory(true);
                ReactTooltip.rebuild();
              }}
            >
              {t('Reinforce')}
            </PickerTitleCategory>
          </div>
          <div className="flex my-1 ">
            {!reinforceCategory ? (
              <>
                <BigOptionButton
                  imgSrc={F1Img}
                  title="F1"
                  handleClick={() => {
                    dispatch(
                      addOrUpdateCrossPieceAction(
                        selectorPositionCP,
                        selectedInferior,
                        CrossPieceType.UpperPaneLowerPane,
                        false,
                      ),
                    );
                  }}
                  handleDoubleClick={() => {
                    dispatch(setColorsPanelAction());
                  }}
                  handleHover={() => {
                    dispatch(
                      addOrUpdateCrossPieceAction(
                        selectorPositionCP,
                        selectedInferior,
                        CrossPieceType.UpperPaneLowerPane,
                        false,
                        true,
                      ),
                    );
                  }}
                  selected={isSelected(CrossPieceType.UpperPaneLowerPane)}
                  enable={isEnable(CrossPieceType.UpperPaneLowerPane)}
                  disableMessage={t(
                    "The crossrail can't be set at the position you selected",
                  )}
                  onLeave={onLeave}
                  infoMessage={t(
                    'Crossrail for panes only, placed above and/or below',
                  )}
                  smallTitle={false}
                />
                <BigOptionButton
                  imgSrc={G1Img}
                  title="G1"
                  handleClick={() => {
                    dispatch(
                      addOrUpdateCrossPieceAction(
                        selectorPositionCP,
                        selectedInferior,
                        CrossPieceType.UpperDoorsLowerDoors,
                        false,
                      ),
                    );
                  }}
                  handleDoubleClick={() => {
                    dispatch(setColorsPanelAction());
                  }}
                  handleHover={() => {
                    dispatch(
                      addOrUpdateCrossPieceAction(
                        selectorPositionCP,
                        selectedInferior,
                        CrossPieceType.UpperDoorsLowerDoors,
                        false,
                        true,
                      ),
                    );
                  }}
                  selected={isSelected(CrossPieceType.UpperDoorsLowerDoors)}
                  enable={isEnable(CrossPieceType.UpperDoorsLowerDoors)}
                  disableMessage={t(
                    "The crossrail can't be set at the position you selected",
                  )}
                  onLeave={onLeave}
                  infoMessage={t(
                    'Crossrail for panes or doors, placed above and/or below',
                  )}
                  smallTitle={false}
                />
                <BigOptionButton
                  imgSrc={F2Img}
                  title="F2"
                  handleClick={() => {
                    dispatch(
                      addOrUpdateCrossPieceAction(
                        selectorPositionCP,
                        selectedInferior,
                        CrossPieceType.UpperEmptyLowerPane,
                        false,
                      ),
                    );
                  }}
                  handleDoubleClick={() => {
                    dispatch(setColorsPanelAction());
                  }}
                  handleHover={() => {
                    dispatch(
                      addOrUpdateCrossPieceAction(
                        selectorPositionCP,
                        selectedInferior,
                        CrossPieceType.UpperEmptyLowerPane,
                        false,
                        true,
                      ),
                    );
                  }}
                  selected={isSelected(CrossPieceType.UpperEmptyLowerPane)}
                  enable={isEnable(CrossPieceType.UpperEmptyLowerPane)}
                  disableMessage={t(
                    "The crossrail can't be set at the position you selected",
                  )}
                  onLeave={onLeave}
                  infoMessage={t(
                    'Smooth crossrail for panes only, placed only below',
                  )}
                  smallTitle={false}
                />
                <BigOptionButton
                  imgSrc={G2Img}
                  title="G2"
                  handleClick={() => {
                    dispatch(
                      addOrUpdateCrossPieceAction(
                        selectorPositionCP,
                        selectedInferior,
                        CrossPieceType.UpperEmptyLowerDoors,
                        false,
                      ),
                    );
                  }}
                  handleDoubleClick={() => {
                    dispatch(setColorsPanelAction());
                  }}
                  handleHover={() => {
                    dispatch(
                      addOrUpdateCrossPieceAction(
                        selectorPositionCP,
                        selectedInferior,
                        CrossPieceType.UpperEmptyLowerDoors,
                        false,
                        true,
                      ),
                    );
                  }}
                  selected={isSelected(CrossPieceType.UpperEmptyLowerDoors)}
                  enable={isEnable(CrossPieceType.UpperEmptyLowerDoors)}
                  disableMessage={t(
                    "The crossrail can't be set at the position you selected",
                  )}
                  onLeave={onLeave}
                  infoMessage={t(
                    'Smooth crossrail for panes or doors, placed only below',
                  )}
                  smallTitle={false}
                />
              </>
            ) : (
              <>
                <BigOptionButton
                  imgSrc={G1RImg}
                  title="G1R"
                  handleClick={() => {
                    dispatch(
                      addOrUpdateCrossPieceAction(
                        selectorPositionCP,
                        selectedInferior,
                        CrossPieceType.UpperDoorsLowerDoors,
                        true,
                      ),
                    );
                  }}
                  handleDoubleClick={() => {
                    dispatch(setColorsPanelAction());
                  }}
                  handleHover={() => {
                    dispatch(
                      addOrUpdateCrossPieceAction(
                        selectorPositionCP,
                        selectedInferior,
                        CrossPieceType.UpperDoorsLowerDoors,
                        true,
                        true,
                      ),
                    );
                  }}
                  selected={isSelected(CrossPieceType.UpperDoorsLowerDoors)}
                  enable={isEnable(CrossPieceType.UpperDoorsLowerDoors)}
                  disableMessage={t(
                    "The crossrail can't be set at the position you selected",
                  )}
                  onLeave={onLeave}
                  infoMessage={t(
                    'Crossrail for panes or doors, placed above and/or below',
                  )}
                  smallTitle={false}
                />
                <BigOptionButton
                  imgSrc={G2RImg}
                  title="G2R"
                  handleClick={() => {
                    dispatch(
                      addOrUpdateCrossPieceAction(
                        selectorPositionCP,
                        selectedInferior,
                        CrossPieceType.UpperEmptyLowerDoors,
                        true,
                      ),
                    );
                  }}
                  handleDoubleClick={() => {
                    dispatch(setColorsPanelAction());
                  }}
                  handleHover={() => {
                    dispatch(
                      addOrUpdateCrossPieceAction(
                        selectorPositionCP,
                        selectedInferior,
                        CrossPieceType.UpperEmptyLowerDoors,
                        true,
                        true,
                      ),
                    );
                  }}
                  selected={isSelected(CrossPieceType.UpperEmptyLowerDoors)}
                  enable={isEnable(CrossPieceType.UpperEmptyLowerDoors)}
                  disableMessage={t(
                    "The crossrail can't be set at the position you selected",
                  )}
                  onLeave={onLeave}
                  infoMessage={t(
                    'Smooth crossrail for panes or doors, placed only below',
                  )}
                  smallTitle={false}
                />
              </>
            )}
          </div>
        </div>
        <div className="w-1/12 flex justify-center items-center">
          <div>
            <FiInfo
              className="h-6 w-6"
              data-tip={t('infoCrossPiecePanel')}
              data-for="infoCP"
            />
          </div>
        </div>
      </div>
      <ReactTooltip id="infoCP" html place="bottom" type="light" />
      <ReactTooltip id="disableInfoMsg" place="bottom" type="warning" />
      <ReactTooltip
        id="removeCrossPieceInfo"
        place="bottom"
        type="light"
        disable={!canDelete}
      >
        <div className="flex w-56 justify-center">
          <div>
            <AiOutlineWarning className="h-8 w-8 text-orange mr-2 -ml-2 mt-2" />
          </div>
          <div>{t('removeCrossPieceMsg')}</div>
        </div>
      </ReactTooltip>
      <ReactTooltip
        id="cannotRemoveCrossPieceInfo"
        place="bottom"
        type="dark"
        disable={canDelete}
      >
        <div className="flex w-56 justify-center">
          <div>{t('cannotRemoveCrossPieceInfo')}</div>
        </div>
      </ReactTooltip>
      <ReactTooltip
        id="cannotRemoveDrillCrossPieceInfo"
        place="bottom"
        type="dark"
        disable={canRemoveDrill}
      >
        <div className="flex w-56 justify-center">
          <div>{t('cannotRemoveDrillCrossPieceInfo')}</div>
        </div>
      </ReactTooltip>
    </Panel>
  );
}

export default CrossPiecePanel;
